import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface UploadedImage {
  dataUrl: string;
  fileName: string;
  s3Key?: string;
}

export interface CustomizationData {
  designId?: string; // Reference to Design record (T-shirt designer)
  uploadedImages?: UploadedImage[]; // Direct image uploads (non-designer products)
  orderDescription?: string; // Custom instructions from customer
  selectedStyleImage?: string; // Selected product style/variant image (e.g. candle designs)
}

export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  productNameEn?: string | null;
  productImage?: string;
  productCategory: string;
  size?: string;
  color?: string;
  colorName?: string;
  material?: string;
  shape?: string;
  variant?: Record<string, unknown>;
  quantity: number;
  customization: CustomizationData;
  unitPrice: number;
  totalPrice: number;
}

interface CartStore {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'id'>) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

const generateCartItemId = (): string => {
  return `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (item) =>
        set((state) => {
          const newItem: CartItem = {
            ...item,
            id: generateCartItemId(),
          };
          return { items: [...state.items, newItem] };
        }),
      removeItem: (itemId) =>
        set((state) => ({
          items: state.items.filter((i) => i.id !== itemId),
        })),
      updateQuantity: (itemId, quantity) =>
        set((state) => ({
          items:
            quantity <= 0
              ? state.items.filter((i) => i.id !== itemId)
              : state.items.map((i) =>
                  i.id === itemId
                    ? { ...i, quantity, totalPrice: i.unitPrice * quantity }
                    : i
                ),
        })),
      clearCart: () => set({ items: [] }),
      getTotalItems: () => {
        const state = get();
        return state.items.reduce((total, item) => total + item.quantity, 0);
      },
      getTotalPrice: () => {
        const state = get();
        return state.items.reduce((total, item) => total + item.totalPrice, 0);
      },
    }),
    {
      name: 'gulart-cart',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          // Strip base64 data from uploadedImages (non-designer flow) before persisting
          const cloned = JSON.parse(JSON.stringify(value));
          if (cloned?.state?.items) {
            cloned.state.items = cloned.state.items.map((item: CartItem) => ({
              ...item,
              customization: {
                ...item.customization,
                uploadedImages: item.customization?.uploadedImages?.map(
                  (img) => ({
                    fileName: img.fileName,
                    dataUrl: '', // stripped for localStorage quota
                    s3Key: img.s3Key,
                  })
                ),
              },
            }));
          }
          try {
            localStorage.setItem(name, JSON.stringify(cloned));
          } catch (e) {
            console.warn('Cart persist failed (quota exceeded):', e);
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
